import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/StatusPill";
import { fmtDateTime, formatMoney, durationHours } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle2, DollarSign, PlayCircle, StopCircle, Ban, Music2, User, Building, Sparkles } from "lucide-react";

export default function BookingDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { data, refetch, isLoading } = useQuery({ queryKey: ["booking", id], queryFn: () => apiGet(`/bookings/${id}`) });
    const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: () => apiGet("/locations"), enabled: !!data?.room?.location_id });
    const { data: clientActivity = [] } = useQuery({ queryKey: ["client-activity", data?.client?.id], queryFn: () => apiGet(`/clients/${data.client.id}/interactions`, { limit: 8 }), enabled: !!data?.client?.id });

    if (isLoading) return <div className="p-6">Loading…</div>;
    if (!data) return <div className="p-6">Not found.</div>;

    const { booking, client, room, service, engineer, invoice, timeline } = data;
    const location = locations.find((item) => item.id === room?.location_id);

    const doAction = async (path, body) => {
        try {
            await apiPost(path, body || {});
            toast.success("Updated");
            refetch();
            qc.invalidateQueries({ queryKey: ["bookings-all"] });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    };

    const markDepositPaid = async () => {
        if (!invoice) return toast.error("No invoice attached");
        try {
            await apiPost(`/invoices/${invoice.id}/mark-paid`, { is_deposit: true });
            toast.success("Deposit recorded — booking confirmed");
            refetch();
        } catch (e) {
            toast.error("Failed");
        }
    };

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            <PageHeader
                title={`${service?.name || "Session"} — ${client?.name || "Client"}`}
                subtitle={`${fmtDateTime(booking.start_utc)} · ${durationHours(booking.start_utc, booking.end_utc)}h · ${location?.name || "Location not set"} · ${room?.name || "Room not set"}`}
                eyebrow="Session / Booking"
                backTo="/bookings"
                backLabel="Back to Bookings"
                testId="booking-detail-header"
                actions={<StatusPill status={booking.status} />}
            />

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card className="p-4 border-border">
                        <h3 className="font-display text-base font-semibold mb-3">Actions</h3>
                        <div className="flex flex-wrap gap-2">
                            {booking.status === "pending" && <Button onClick={markDepositPaid} data-testid="booking-mark-deposit-paid"><DollarSign className="mr-2 h-4 w-4" />Record deposit paid</Button>}
                            {booking.status === "pending" && <Button variant="outline" onClick={() => doAction(`/bookings/${id}/confirm`)} data-testid="booking-confirm"><CheckCircle2 className="mr-2 h-4 w-4" />Confirm without payment</Button>}
                            {booking.status === "confirmed" && <Button onClick={() => doAction(`/bookings/${id}/check-in`)} data-testid="booking-checkin"><PlayCircle className="mr-2 h-4 w-4" />Check in</Button>}
                            {booking.status === "in_progress" && <Button onClick={() => doAction(`/bookings/${id}/complete`)} data-testid="booking-complete"><StopCircle className="mr-2 h-4 w-4" />Complete session</Button>}
                            {["pending", "confirmed"].includes(booking.status) && <Button variant="destructive" onClick={() => doAction(`/bookings/${id}/cancel`, { reason: "Cancelled from detail page" })} data-testid="booking-cancel"><Ban className="mr-2 h-4 w-4" />Cancel</Button>}
                        </div>
                    </Card>

                    <Card className="p-4 border-border">
                        <h3 className="font-display text-base font-semibold mb-3">Notes</h3>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{booking.notes || "No notes."}</p>
                    </Card>

                    <Card className="p-4 border-border">
                        <h3 className="font-display text-base font-semibold mb-3">Timeline</h3>
                        <ul className="space-y-3">
                            {(timeline || []).length === 0 && <li className="text-sm text-muted-foreground">No activity yet.</li>}
                            {(timeline || []).map((t) => (
                                <li key={t.id} className="flex gap-3 text-sm">
                                    <span className="mt-1 h-2 w-2 rounded-full bg-primary/70" />
                                    <div className="flex-1">
                                        <div className="font-medium">{t.action.replace(/_/g, " ")}</div>
                                        <div className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)} · {t.actor_name || "System"}</div>
                                        {t.metadata?.text && <div className="mt-1 text-xs">{t.metadata.text}</div>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </Card>
                </div>
                <div className="space-y-6">
                    <Card className="p-4 border-border">
                        <h3 className="font-display text-base font-semibold mb-3">Session</h3>
                        <ul className="space-y-2 text-sm">
                            <li className="flex items-center gap-2"><Music2 className="h-4 w-4 text-muted-foreground" /> {service?.name}</li>
                            <li className="flex items-center gap-2"><Building className="h-4 w-4 text-muted-foreground" /> {location?.name || "Location not set"} · {room?.name || "Room not set"}</li>
                            <li className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-muted-foreground" /> {engineer?.name || "No engineer"}</li>
                            <li className="flex items-center gap-2"><User className="h-4 w-4 text-muted-foreground" /> <Link to={`/clients/${client?.id}`} className="text-primary hover:underline">{client?.name}</Link></li>
                        </ul>
                    </Card>
                    <Card className="p-4 border-border">
                        <h3 className="font-display text-base font-semibold mb-3">Recent client activity</h3>
                        <ul className="space-y-3">
                            {clientActivity.length === 0 && <li className="text-sm text-muted-foreground">No recent activity for this client.</li>}
                            {clientActivity.slice(0, 6).map((item) => <li key={item.id} className="text-sm"><div className="font-medium">{item.subject || String(item.type || item.action || "Activity").replace(/_/g, " ")}</div><div className="text-xs text-muted-foreground">{fmtDateTime(item.created_at)}{item.actor_name ? ` · ${item.actor_name}` : ""}</div>{(item.body || item.metadata?.text) && <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{item.body || item.metadata.text}</div>}</li>)}
                        </ul>
                        {client?.id && <Link to={`/clients/${client.id}`} className="mt-3 inline-block text-xs text-primary hover:underline">Open full client history</Link>}
                    </Card>
                    <Card className="p-4 border-border">
                        <h3 className="font-display text-base font-semibold mb-3">Financials</h3>
                        <ul className="space-y-1 text-sm tabular-nums">
                            <li className="flex justify-between"><span>Session price</span><span>{formatMoney(booking.price_total)}</span></li>
                            <li className="flex justify-between"><span>Deposit required</span><span>{formatMoney(booking.deposit_required)}</span></li>
                            <li className="flex justify-between"><span>Deposit paid</span><span>{formatMoney(booking.deposit_paid)}</span></li>
                            <li className="flex justify-between font-medium"><span>Paid total</span><span>{formatMoney(booking.paid_total)}</span></li>
                        </ul>
                        {invoice && (
                            <Button variant="outline" className="w-full mt-3" onClick={() => navigate(`/invoices/${invoice.id}`)} data-testid="booking-open-invoice">Open invoice {invoice.number}</Button>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
}
